// pages/Admin/BillingManagement.tsx
import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  TextField,
  Chip,
  TableSortLabel,
  TablePagination,
  Modal,
  Divider,
  Tabs,
  Tab,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useAdmin } from "../../context/AdminContext";
import { formatCurrency } from "../../utils/utils";
import { getUserUsageDetails } from "../../client/api";

type SortField =
  | "email"
  | "name"
  | "sora2Count"
  | "seedanceCount"
  | "veo3Count"
  | "heygenCount"
  | "ivAvatarVideoCount"
  | "customPhotoAvatarsCount"
  | "totalEstimatedCostUSD";

const modalStyle = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 700,
  maxHeight: "85vh",
  overflow: "auto",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 4,
  borderRadius: 2,
};

export const BillingManagement: React.FC = () => {
  const { usageOverview, fetchUsageOverview, loading } = useAdmin();

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [sortBy, setSortBy] = useState<SortField>("totalEstimatedCostUSD");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [overviewPeriod, setOverviewPeriod] = useState<"allTime" | "currentMonth" | "lastMonth">("allTime");

  // Modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailData, setDetailData] = useState<any>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0); // 0=All Time, 1=Current Month, 2=Last Month

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(0);
    }, 500);
    return () => clearTimeout(timer);
  }, [search]);

  // Fetch data when params change
  useEffect(() => {
    fetchUsageOverview({
      search: debouncedSearch || undefined,
      page: page + 1,
      limit: rowsPerPage,
      sortBy,
      sortOrder,
    });
  }, [debouncedSearch, page, rowsPerPage, sortBy, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  const handleRowClick = useCallback(async (userId: string) => {
    setDetailModalOpen(true);
    setDetailLoading(true);
    setDetailData(null);
    setDetailTab(0);
    try {
      const data = await getUserUsageDetails(userId);
      setDetailData(data);
    } catch (error) {
      console.error("Error fetching user usage details:", error);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const handleCloseModal = () => {
    setDetailModalOpen(false);
    setDetailData(null);
  };

  const users = usageOverview?.users || [];
  const pagination = usageOverview?.pagination;

  // Helper to pick the right count based on selected overview period
  const getCount = (row: any, allTimeKey: string, monthlyKey: string, lastMonthKey: string) => {
    if (overviewPeriod === "currentMonth") return row[monthlyKey] || 0;
    if (overviewPeriod === "lastMonth") return row[lastMonthKey] || 0;
    return row[allTimeKey] || 0;
  };

  // Helper to pick the right estimated cost based on selected overview period
  const getCost = (row: any) => {
    if (overviewPeriod === "currentMonth") return row.monthlyEstimatedCostUSD || 0;
    if (overviewPeriod === "lastMonth") return row.lastMonthEstimatedCostUSD || 0;
    return row.totalEstimatedCostUSD || 0;
  };

  // Get the active time period data for the modal
  const getActivePeriodData = () => {
    if (!detailData) return null;
    if (detailTab === 0) return detailData.allTime;
    if (detailTab === 1) return detailData.currentMonth;
    return detailData.lastMonth;
  };

  const periodData = getActivePeriodData();

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom sx={{ mb: 3 }}>
        User Cost Analytics
      </Typography>

      {/* Filters */}
      <Box sx={{ display: "flex", gap: 2, mb: 3, flexWrap: "wrap", alignItems: "center" }}>
        <TextField
          size="small"
          label="Search users"
          placeholder="Name or email..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputLabelProps={{ sx: { color: "#555" } }}
          sx={{ width: 280 }}
        />
        <ToggleButtonGroup
          value={overviewPeriod}
          exclusive
          onChange={(_, val) => { if (val) setOverviewPeriod(val); }}
          size="small"
        >
          <ToggleButton value="allTime" sx={{ textTransform: "none", px: 2 }}>All Time</ToggleButton>
          <ToggleButton value="currentMonth" sx={{ textTransform: "none", px: 2 }}>Current Month</ToggleButton>
          <ToggleButton value="lastMonth" sx={{ textTransform: "none", px: 2 }}>Last Month</ToggleButton>
        </ToggleButtonGroup>
        {loading && <CircularProgress size={24} />}
      </Box>

      {/* Table */}
      <TableContainer component={Paper} sx={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead
            sx={{
              "& .MuiTableSortLabel-root": { color: "#333" },
              "& .MuiTableSortLabel-root:hover": { color: "#333" },
              "& .MuiTableSortLabel-root.Mui-active": { color: "#333" },
              "& .MuiTableSortLabel-icon": { color: "#333 !important" },
            }}
          >
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={sortBy === "email"}
                  direction={sortBy === "email" ? sortOrder : "asc"}
                  onClick={() => handleSort("email")}
                >
                  User
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "sora2Count"}
                  direction={sortBy === "sora2Count" ? sortOrder : "asc"}
                  onClick={() => handleSort("sora2Count")}
                >
                  Sora2
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "seedanceCount"}
                  direction={sortBy === "seedanceCount" ? sortOrder : "asc"}
                  onClick={() => handleSort("seedanceCount")}
                >
                  Seedance
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "veo3Count"}
                  direction={sortBy === "veo3Count" ? sortOrder : "asc"}
                  onClick={() => handleSort("veo3Count")}
                >
                  Veo3
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "heygenCount"}
                  direction={sortBy === "heygenCount" ? sortOrder : "asc"}
                  onClick={() => handleSort("heygenCount")}
                >
                  HeyGen Videos
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "ivAvatarVideoCount"}
                  direction={sortBy === "ivAvatarVideoCount" ? sortOrder : "asc"}
                  onClick={() => handleSort("ivAvatarVideoCount")}
                >
                  IV Avatar Videos
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <TableSortLabel
                  active={sortBy === "customPhotoAvatarsCount"}
                  direction={sortBy === "customPhotoAvatarsCount" ? sortOrder : "asc"}
                  onClick={() => handleSort("customPhotoAvatarsCount")}
                >
                  Photo Avatars
                </TableSortLabel>
              </TableCell>
              <TableCell align="right">
                <Tooltip
                  title="Estimated cost of API usage for prompt-to-video generation (Sora2, Seedance, Veo3)"
                  placement="top"
                >
                  <TableSortLabel
                    active={sortBy === "totalEstimatedCostUSD"}
                    direction={sortBy === "totalEstimatedCostUSD" ? sortOrder : "asc"}
                    onClick={() => handleSort("totalEstimatedCostUSD")}
                  >
                    Kie API Usage Cost
                  </TableSortLabel>
                </Tooltip>
              </TableCell>
              <TableCell align="center">
                <Tooltip title="Success rate for Sora2 / Seedance / Veo3" placement="top">
                  <span>Success Rate</span>
                </Tooltip>
              </TableCell>
              <TableCell align="center">Premium</TableCell>
              <TableCell align="right">
                <Tooltip
                  title="Sum of pricePerUGC x videoLimit across all premium workspaces"
                  placement="top"
                >
                  <span>Premium Monthly Subscription</span>
                </Tooltip>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 && !loading ? (
              <TableRow>
                <TableCell colSpan={11} align="center" sx={{ py: 4, color: "#999" }}>
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((row: any) => (
                <TableRow
                  key={row.userId}
                  hover
                  onClick={() => handleRowClick(row.userId)}
                  sx={{ cursor: "pointer" }}
                >
                  <TableCell>
                    <Tooltip title="Click to view detailed usage breakdown" arrow>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography variant="body2" fontWeight={500}>
                          {row.userEmail}
                        </Typography>
                        <InfoOutlinedIcon sx={{ fontSize: 16, color: "#888" }} />
                      </Box>
                    </Tooltip>
                    {row.userName && (
                      <Typography variant="caption" color="textSecondary">
                        {row.userName}
                      </Typography>
                    )}
                  </TableCell>
                  <CountCell value={getCount(row, "sora2Count", "monthlySora2Count", "lastMonthSora2Count")} monthly={overviewPeriod === "allTime" ? row.monthlySora2Count : undefined} />
                  <CountCell value={getCount(row, "seedanceCount", "monthlySeedanceCount", "lastMonthSeedanceCount")} monthly={overviewPeriod === "allTime" ? row.monthlySeedanceCount : undefined} />
                  <CountCell value={getCount(row, "veo3Count", "monthlyVeo3Count", "lastMonthVeo3Count")} monthly={overviewPeriod === "allTime" ? row.monthlyVeo3Count : undefined} />
                  <CountCell value={getCount(row, "heygenCount", "monthlyHeygenCount", "lastMonthHeygenCount")} monthly={overviewPeriod === "allTime" ? row.monthlyHeygenCount : undefined} />
                  <CountCell value={getCount(row, "ivAvatarVideoCount", "monthlyIvAvatarVideoCount", "lastMonthIvAvatarVideoCount")} monthly={overviewPeriod === "allTime" ? row.monthlyIvAvatarVideoCount : undefined} />
                  <CountCell value={getCount(row, "customPhotoAvatarsCount", "monthlyCustomPhotoAvatarsCount", "lastMonthCustomPhotoAvatarsCount")} monthly={overviewPeriod === "allTime" ? row.monthlyCustomPhotoAvatarsCount : undefined} />
                  <TableCell align="right" sx={{ fontWeight: 500 }}>
                    {formatCurrency(getCost(row))}
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="caption" sx={{ fontSize: "11px", whiteSpace: "nowrap" }}>
                      {formatRate(row.successRate?.sora2)} / {formatRate(row.successRate?.seedance)}{" "}
                      / {formatRate(row.successRate?.veo3)}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Chip
                      label={row.hasPremiumWorkspace ? "Yes" : "No"}
                      size="small"
                      color={row.hasPremiumWorkspace ? "success" : "default"}
                      variant={row.hasPremiumWorkspace ? "filled" : "outlined"}
                    />
                  </TableCell>
                  <TableCell align="right">
                    {row.premiumMonthlyTotal > 0 ? formatCurrency(row.premiumMonthlyTotal) : "—"}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {pagination && (
          <TablePagination
            component="div"
            count={pagination.total || 0}
            page={page}
            onPageChange={(_, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        )}
      </TableContainer>

      {/* User Detail Modal */}
      <Modal open={detailModalOpen} onClose={handleCloseModal}>
        <Box sx={modalStyle}>
          {detailLoading ? (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
              <CircularProgress />
            </Box>
          ) : detailData ? (
            <>
              {/* User Info */}
              <Typography variant="h5" fontWeight={600} gutterBottom>
                {detailData.user?.name || "Unknown"}
              </Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                {detailData.user?.email}
              </Typography>

              <Divider sx={{ my: 2 }} />

              {/* Workspaces */}
              <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                Workspaces ({detailData.workspaceCount || 0})
              </Typography>
              {detailData.workspaces?.length > 0 ? (
                <Box sx={{ mb: 2 }}>
                  {detailData.workspaces.map((ws: any) => (
                    <Box
                      key={ws.id}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ color: "#333" }}>
                        {ws.name}
                      </Typography>
                      {ws.isPremium && (
                        <Chip label={ws.planName || "Premium"} size="small" color="success" />
                      )}
                      {ws.monthlyPrice > 0 && (
                        <Typography variant="caption" sx={{ color: "#666" }}>
                          {formatCurrency(ws.monthlyPrice)}/mo
                        </Typography>
                      )}
                    </Box>
                  ))}
                  {(() => {
                    const totalSubscription = detailData.workspaces.reduce(
                      (sum: number, ws: any) => sum + (ws.monthlyPrice || 0),
                      0
                    );
                    return totalSubscription > 0 ? (
                      <Box
                        sx={{
                          mt: 1,
                          pt: 1,
                          borderTop: "1px solid #eee",
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2" fontWeight={600} sx={{ color: "#333" }}>
                          Total Subscription:
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: "#333" }}>
                          {formatCurrency(totalSubscription)}/mo
                        </Typography>
                      </Box>
                    ) : null;
                  })()}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: "#999", mb: 2 }}>
                  No workspaces
                </Typography>
              )}

              <Divider sx={{ my: 2 }} />

              {/* Time Period Tabs */}
              <Tabs
                value={detailTab}
                onChange={(_, v) => setDetailTab(v)}
                sx={{
                  mb: 2,
                  "& .MuiTab-root": { color: "#555" },
                  "& .MuiTab-root.Mui-selected": { color: "#1976d2" },
                }}
                variant="fullWidth"
              >
                <Tab label="All Time" />
                <Tab label="Current Month" />
                <Tab label="Last Month" />
              </Tabs>

              {periodData ? (
                <>
                  {/* Prompt to Video Section */}
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    Prompt to Video
                  </Typography>

                  {periodData.sora2 && periodData.sora2.total > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: "#333" }}
                        gutterBottom
                      >
                        Sora2 ({periodData.sora2.total} total)
                      </Typography>
                      <EngineBreakdownTable
                        data={periodData.sora2.byType}
                        labels={{ standard: "Standard", pro: "Pro", storyboard: "Storyboard" }}
                      />
                    </Box>
                  )}

                  {periodData.seedance && periodData.seedance.total > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: "#333" }}
                        gutterBottom
                      >
                        Seedance ({periodData.seedance.total} total)
                      </Typography>
                      <EngineBreakdownTable
                        data={periodData.seedance.byType}
                        labels={{ v1: "V1", v15pro: "1.5 Pro", v2: "2.0", v2Fast: "2.0 Fast" }}
                      />
                    </Box>
                  )}

                  {periodData.veo3 && periodData.veo3.total > 0 && (
                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ color: "#333" }}
                        gutterBottom
                      >
                        Veo3 ({periodData.veo3.total} total)
                      </Typography>
                      <EngineBreakdownTable
                        data={periodData.veo3.byType}
                        labels={{ fast: "Fast", quality: "Quality" }}
                      />
                    </Box>
                  )}

                  {/* Prompt to Video Totals */}
                  <Box sx={{ display: "flex", gap: 4, mt: 1, mb: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Total Credits Spent
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#333" }}>
                        {(periodData.totalCreditsSpent || 0).toLocaleString()}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Total Estimated Cost
                      </Typography>
                      <Typography variant="h6" fontWeight={600} color="error">
                        {formatCurrency(periodData.totalCostUSD || 0)}
                      </Typography>
                    </Box>
                  </Box>

                  <Divider sx={{ my: 2 }} />

                  {/* HeyGen Section */}
                  <Typography variant="subtitle1" fontWeight={600} gutterBottom>
                    HeyGen
                  </Typography>
                  <Box sx={{ display: "flex", gap: 4, mb: 1 }}>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Generated Videos
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#333" }}>
                        {periodData.heygen?.total || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        IV Avatar Videos
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#333" }}>
                        {periodData.ivAvatarVideos?.total || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Video Agent
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#333" }}>
                        {periodData.videoAgentVideos?.total || 0}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" sx={{ color: "#666" }}>
                        Custom Photo Avatars
                      </Typography>
                      <Typography variant="h6" sx={{ color: "#333" }}>
                        {periodData.customPhotoAvatars?.total || 0}
                      </Typography>
                    </Box>
                  </Box>
                </>
              ) : (
                <Typography variant="body2" sx={{ color: "#999", textAlign: "center", py: 2 }}>
                  No data for this period
                </Typography>
              )}
            </>
          ) : (
            <Typography color="error">Failed to load user details</Typography>
          )}
        </Box>
      </Modal>
    </Box>
  );
};

// Small reusable table for engine sub-type breakdowns
const EngineBreakdownTable: React.FC<{
  data: Record<string, { count: number; credits: number; cost: number }>;
  labels: Record<string, string>;
}> = ({ data, labels }) => {
  if (!data) return null;
  const keys = Object.keys(labels).filter((k) => data[k]);
  if (keys.length === 0) return null;

  return (
    <Table size="small" sx={{ mb: 1 }}>
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: 600, py: 0.5 }}>Type</TableCell>
          <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>
            Count
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>
            Credits
          </TableCell>
          <TableCell align="right" sx={{ fontWeight: 600, py: 0.5 }}>
            Cost
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {keys.map((key) => (
          <TableRow key={key}>
            <TableCell sx={{ py: 0.5 }}>{labels[key]}</TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>
              {data[key].count}
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>
              {(data[key].credits || 0).toLocaleString()}
            </TableCell>
            <TableCell align="right" sx={{ py: 0.5 }}>
              {formatCurrency(data[key].cost || 0)}
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
};

// Table cell showing count with optional monthly sub-text (only in "All Time" view)
const CountCell: React.FC<{ value: number; monthly?: number }> = ({ value, monthly }) => (
  <TableCell align="right">
    <Typography variant="body2">{value || 0}</Typography>
    {monthly != null && (monthly || 0) > 0 && (
      <Typography variant="caption" color="primary" sx={{ fontSize: "10px" }}>
        +{monthly} this mo.
      </Typography>
    )}
  </TableCell>
);

// Format success rate: null → "—", number → "85%"
const formatRate = (rate: number | null | undefined): string => {
  if (rate == null) return "—";
  return `${rate}%`;
};
